/**
 * useTurnSimulation - Stepwise reveal of a goldfish turn simulation
 *
 * The run is computed once and revealed one turn at a time, so stepping forward
 * never reshuffles the draw pile. Changing the turn options invalidates the run
 * rather than mixing results from two different simulations.
 */
import { useState } from 'react';

import type { SimCard, TurnSimOptions, TurnSimulation } from '@/features/decks/lib/handSim';
import { drawPileFor, simulateTurns } from '@/features/decks/lib/handSim';

// ============================================================================
// Types
// ============================================================================

export interface TurnRun {
  simulation: TurnSimulation;
  options: TurnSimOptions;
  revealed: number;
}

export interface TurnSimulationState {
  /** Null before the first step, and whenever the turn options change */
  run: TurnRun | null;
  /** Turns the run actually covers — shorter than requested when the library runs out */
  turnsSimulated: number;
  atLimit: boolean;
  next: () => void;
  playAll: () => void;
  reset: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useTurnSimulation(
  hand: SimCard[],
  library: SimCard[],
  commanders: SimCard[],
  options: TurnSimOptions,
): TurnSimulationState {
  const [stored, setStored] = useState<TurnRun | null>(null);

  const run = stored && stored.options.turns === options.turns && stored.options.onThePlay === options.onThePlay ? stored : null;
  const turnsSimulated = run?.simulation.actions.length ?? options.turns;

  const reveal = (target: number): void => {
    const simulation = run?.simulation ?? simulateTurns(hand, drawPileFor(library, hand), options, commanders);
    setStored({ simulation, options, revealed: Math.min(target, simulation.actions.length) });
  };

  return {
    run,
    turnsSimulated,
    atLimit: run !== null && run.revealed >= turnsSimulated,
    next: () => reveal((run?.revealed ?? 0) + 1),
    playAll: () => reveal(options.turns),
    reset: () => setStored(null),
  };
}
